import Image from 'next/image';
import Container from 'components/container/container';
import ProductPage from 'components/product';
import ProductFaqSection from 'components/product/product-faq';
import {
  ProductDivider,
  ProductFeatureCollapsible,
  ProductFeatureContainer,
  ProductFeatureLayoutGrid,
  ProductFeatureLayoutGridNode,
  ProductFeatureRequest,
  ProductFeatureShowMoreContainer,
  ProductFeatureShowMoreLink,
  ProductPlan,
  ProductQuote,
} from 'components/product/product.styles';
import { ProductFeatureStatus } from 'components/product/product.types';
import Link from 'next/link';
import { useState } from 'react';
import Plan from 'shared/components/plan';

import { CUBUILD_URL, LIBUM_FEEDBACK, LIBUM_URL, MIKE_LINKEDIN, VSCODE_URL } from 'shared/constants/globals';
import { useTheme } from 'shared/styles/theme';
import { PRODUCTS } from 'shared/data/products';

const POWERON_PRO = PRODUCTS.find((p) => p.id === 'poweron-pro')!;

const POWERON_PRO_FAQ = [
  {
    question: 'What is PowerOn Pro?',
    answer:
      'PowerOn Pro is a complete development toolkit for Symitar PowerOn that runs inside Visual Studio Code, pairing a full PowerOn language server with direct Symitar integration.',
  },
  {
    question: 'Do I still need Symitar Quest to write PowerOn?',
    answer:
      'No. PowerOn Pro connects to Symitar directly, so you can write, validate, and troubleshoot PowerOn without leaving VS Code or switching back to Quest.',
  },
  {
    question: 'What PowerOn language features does it include?',
    answer:
      'A PowerOn language server purpose-built for Symitar — syntax highlighting, autocompletion, go-to-definition, and inline validation as you type.',
  },
  {
    question: 'Is PowerOn Pro free?',
    answer:
      'Yes — the core extension is free on the Visual Studio Code Marketplace. A Plus tier adds advanced capabilities for teams that want more.',
  },
];

export default function PowerOnPro() {
  const { theme } = useTheme();
  const title = 'PowerOn Pro | Libum';
  const description = 'PowerOn Pro is your complete development toolkit for PowerOn, within Visual Studio Code.';
  const keywords =
    'PowerOn Pro,PowerOn extension,PowerOn development,PowerOn language server,PowerOn vscode,Symitar tools,PowerOn,Symitar,Symitar software,Jack Henry software,credit union software';
  const url = `${LIBUM_URL}/poweron-pro`;

  const [showMore, setShowMore] = useState(false);
  const handleShowMoreClick = () => {
    setShowMore(!showMore);
  };

  const getStatusDisplayName = (status: ProductFeatureStatus): string => {
    return status
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  };

  const productSchemaData = {
    name: 'PowerOn Pro',
    description: description,
    category: 'DeveloperApplication',
    operatingSystem: 'Web, Windows, macOS, Linux',
    image: `${LIBUM_URL}/images/3a8f2d1e-7c4b-4e9a-b5f6-8d2e1c3a4b5f.png`,
  };

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="poweron-pro"
      product={productSchemaData}
      breadcrumbs={[
        { name: 'Products', url: `${LIBUM_URL}/products` },
        { name: 'PowerOn Pro', url },
      ]}
      faq={POWERON_PRO_FAQ}
    >
      <div style={{ width: '100%' }}>
        <ProductPage product={POWERON_PRO}>
          <h1>PowerOn Pro</h1>
          <h2>Your development toolkit for PowerOn, within Visual Studio Code.</h2>
          <ProductDivider />
          <p id="overview">
            We thought it&apos;d be awesome to build out a complete language server for PowerOn and couple it with
            direct Symitar integration. At{' '}
            <Link href={CUBUILD_URL} target="_blank">
              CU Build
            </Link>{' '}
            in 2024, we decided to take this on and make it available in one of the world&apos;s most popular IDEs,{' '}
            <Link href={VSCODE_URL} target="_blank">
              Visual Studio Code
            </Link>{' '}
            — and thus, PowerOn Pro was born.
          </p>
          <p>
            Shortly thereafter, we asked the community to use their imagination for features they&apos;d like to see
            added to the extension and we&apos;ve been working on those ever since.
          </p>
          <figure style={{ margin: '32px 0', padding: '0 48px' }}>
            <Image
              src={
                theme === 'dark'
                  ? '/images/3a8f2d1e-7c4b-4e9a-b5f6-8d2e1c3a4b5f.png'
                  : '/images/e7a3c1f5-9d2b-4e6f-8a0c-3b5d7f9e1a2c.png'
              }
              alt="PowerOn Pro in Visual Studio Code with a PowerOn file open"
              width={1160}
              height={714}
              style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
            />
            <figcaption style={{ marginTop: '12px', textAlign: 'center', fontSize: '14px', opacity: 0.8 }}>
              PowerOn Pro in Visual Studio Code
            </figcaption>
          </figure>
          <ProductDivider />
          <ProductFeatureContainer id="features">
            <ProductFeatureLayoutGrid className="featured">
              {POWERON_PRO.features
                .filter((f) => f.isFeatured)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={`isFeatured ${feature.docsLink ? 'has-link' : ''}`}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="image">
                      <Image
                        src={feature.imagePath}
                        alt={`${feature.name} feature screenshot`}
                        width={400}
                        height={250}
                        style={{ maxHeight: '100%', width: 'auto', height: 'auto' }}
                      />
                    </div>
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureLayoutGrid>
              {POWERON_PRO.features
                .filter((f) => !f.isFeatured && !f.isHidden)
                .map((feature) => (
                  <ProductFeatureLayoutGridNode
                    key={feature.id}
                    className={feature.docsLink ? 'has-link' : ''}
                    onClick={() => {
                      feature.docsLink && window.open(feature.docsLink, '_blank');
                    }}
                    id={feature.id}
                  >
                    <div className="content">
                      <div className="name">{feature.name}</div>
                      <div className={`details ${feature.offeringType}`}>
                        <div className="status">
                          <div className={`dot ${feature.status}`} />
                          {getStatusDisplayName(feature.status)}
                        </div>
                        <div className="offering">
                          <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                          {feature.offeringType}
                        </div>
                      </div>
                      <div className="description">{feature.description}</div>
                    </div>
                  </ProductFeatureLayoutGridNode>
                ))}
            </ProductFeatureLayoutGrid>
            <ProductFeatureCollapsible className={showMore ? 'opened' : ''}>
              <div>
                <ProductFeatureLayoutGrid>
                  {POWERON_PRO.features
                    .filter((f) => f.isHidden)
                    .map((feature) => (
                      <ProductFeatureLayoutGridNode
                        key={feature.id}
                        className={feature.docsLink ? 'has-link' : ''}
                        onClick={() => {
                          feature.docsLink && window.open(feature.docsLink, '_blank');
                        }}
                        id={feature.id}
                      >
                        <div className="content">
                          <div className="name">{feature.name}</div>
                          <div className="details">
                            <div className="status">
                              <div className={`dot ${feature.status}`} />
                              {getStatusDisplayName(feature.status)}
                            </div>
                            <div className="offering">
                              <div className={`dot ${feature.offeringType?.toLowerCase()}`} />
                              {feature.offeringType}
                            </div>
                          </div>
                          <div className="description">{feature.description}</div>
                        </div>
                      </ProductFeatureLayoutGridNode>
                    ))}
                </ProductFeatureLayoutGrid>
              </div>
            </ProductFeatureCollapsible>
          </ProductFeatureContainer>
          <ProductFeatureShowMoreContainer>
            <ProductFeatureShowMoreLink onClick={handleShowMoreClick}>
              {showMore ? 'Show less' : 'Show more'}
            </ProductFeatureShowMoreLink>
          </ProductFeatureShowMoreContainer>
          <ProductDivider />
          <ProductQuote>
            &quot;Libum&apos;s development team knows their stuff. Even while the extension was in Alpha, most of the
            features they promised worked well and those that did not, the team addressed them right away.
            <br />
            <br />
            This extension has saved me a lot of development time because of its ease of use and smooth integration with
            Symitar. It&apos;s very convenient as you can get away with not having to switch from VS Code to Symitar
            Quest and back again to troubleshoot your applications.
            <br />
            <br />
            Did I mention, great technical support team?&quot;
            <br />
            <br />{' '}
            <i>
              <Link href={MIKE_LINKEDIN} target="_blank">
                Louie Medina, Developer @ Citadel Credit Union
              </Link>
            </i>
          </ProductQuote>
          <ProductDivider />
          <ProductPlan id="offerings">
            <Plan
              header={POWERON_PRO.plan.header}
              features={POWERON_PRO.plan.features}
              offerings={POWERON_PRO.plan.offerings}
            />
          </ProductPlan>
          <ProductDivider />
          <ProductFaqSection faq={POWERON_PRO_FAQ} />
          <ProductDivider />
          <ProductFeatureRequest>
            <h3>Have a feature you&apos;d like to see in PowerOn Pro?</h3>
            <Link href={LIBUM_FEEDBACK} target="_blank">
              Submit Your Idea
            </Link>
          </ProductFeatureRequest>
        </ProductPage>
      </div>
    </Container>
  );
}
